import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Configuration for tab visibility refresh
const TAB_VISIBILITY_CONFIG = {
  enabled: true, // Master switch to enable/disable the feature
  debug: true,   // Enable debug logging
  // Note: Routes are no longer used - refresh is based on sessionStorage 'isCameraPage'
};

const useTabVisibilityRefresh = () => {
  const location = useLocation();
  const lastVisibilityState = useRef(document.visibilityState);
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Early return if feature is disabled
    if (!TAB_VISIBILITY_CONFIG.enabled) {
      return;
    }

    const handleVisibilityChange = () => {
      // Skip on initial mount
      if (isInitialMount.current) {
        isInitialMount.current = false;
        lastVisibilityState.current = document.visibilityState;
        return;
      }

      if (TAB_VISIBILITY_CONFIG.debug) {
        console.log('Visibility changed:', {
          from: lastVisibilityState.current,
          to: document.visibilityState,
          currentPath: location.pathname
        });
      }

      // Check if user is returning to the tab (from hidden to visible)
      if (lastVisibilityState.current === 'hidden' && document.visibilityState === 'visible') {
        // Check if sessionStorage contains isCameraPage flag
        const isCameraPage = sessionStorage.getItem('isCameraPage');
        
        if (TAB_VISIBILITY_CONFIG.debug) {
          console.log('Tab visibility check:', {
            currentPath: location.pathname,
            isCameraPage: isCameraPage
          });
        }

        // Only refresh if we're on a camera page
        if (isCameraPage === 'true') {
          if (TAB_VISIBILITY_CONFIG.debug) {
            console.log('Tab became visible on camera page, refreshing page');
          }
          sessionStorage.setItem('isPostReload', 'true');
          window.location.reload();
        }
      }

      lastVisibilityState.current = document.visibilityState;
    };

    // Add event listener for visibility change
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [location.pathname]);

  return null;
};

export default useTabVisibilityRefresh;
